import { PreloadingStrategy, Route, CanActivateFn } from '@angular/router';
import {
	catchError,
	filter,
	from,
	map,
	mergeMap,
	Observable,
	of,
	Subject,
	switchMap,
	take
} from 'rxjs';
import { Store } from '@ngrx/store';
import { selectIsAuthenticated } from 'app/store/auth-proxy/auth-proxy.selectors';
import { Injectable, Injector, runInInjectionContext } from '@angular/core';
import { isArrayNullOrEmpty } from 'app/common/utils/utils.array';
import { schedulePreload } from 'app/core/routing/preload.utils';

@Injectable()
export class AuthenticatedPreloadingStrategy implements PreloadingStrategy {
	private isPreloadInProgress: boolean = false;
	private preloadSubject:      Subject<void> = new Subject();
	private preload$:            Observable<void> = this.preloadSubject.asObservable();

	constructor(
		private readonly store: Store,
		private readonly injector: Injector
	) {}

	preload(
		route: Route,
		load: () => Observable<unknown>
	): Observable<unknown> {
		return this.store.select(selectIsAuthenticated).pipe(
			filter(isAuthenticated => isAuthenticated),
			take(1),
			switchMap(() => this.canActivateRoute(route)),
			filter(canActivate => canActivate),
			switchMap(() => {
				const nextPreloadSubject: Subject<void> = new Subject();

				schedulePreload(this.preload$, load, nextPreloadSubject);

				this.preload$ = nextPreloadSubject.asObservable();

				if (!this.isPreloadInProgress) {
					this.preloadSubject.next();
				}

				this.preloadSubject = nextPreloadSubject;

				this.isPreloadInProgress = true;

				return of(null);
			})
		);
	}

	private canActivateRoute(route: Route): Observable<boolean> {
		if (isArrayNullOrEmpty(route.canActivate)) {
			return of(true);
		}

		const guards$ = route.canActivate.map((guard: CanActivateFn) => {
			return runInInjectionContext(this.injector, () =>
				guard(null, null)
			) as Observable<boolean>;
		});

		return from(guards$).pipe(
			mergeMap(guard$ =>
				guard$.pipe(
					map(result => {
						if (!result) {
							throw new Error('Guard failed');
						}

						return result;
					})
				)
			),
			catchError(() => of(false))
		);
	}
}
